// Digital Corporate Chef: the flagship AI consultant, exclusive to
// annual (yearly) Pro subscribers. Floating gold rimmed chat panel
// with V2 True Glass styling. Mounted globally by Layout but self
// disables when the caller is not on an annual Pro plan.

import { useEffect, useRef, useState, useCallback } from "react";
import { Sparkles, X, Send, Trash2, ChefHat, Loader2, Lock, ArrowRight, Globe2, Check, GripHorizontal, Camera } from "lucide-react";
import AtomCore from "@/components/AtomCore";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const STARTERS = [
  "Where is my margin bleeding this week?",
  "How can I cut labour without hurting service?",
  "Which dish should I re-price first?",
  "What is my worst yield ingredient right now?",
];

// Persisted drag + resize hook for the Corporate Chef panel. Position and
// size are remembered in localStorage so a chef who moves the window to
// their preferred corner keeps it there across page loads. Defaults keep
// the original bottom right anchor for first time users. Works with both
// mouse and touch, so a tablet on the pass can drag the panel too.
const CHEF_LAYOUT_KEY = "cs:corporate-chef:layout";

// Force initial position + size to fit whatever viewport we are opening
// on. Prevents a saved desktop layout from parking the Chef panel off
// screen when the operator returns on their phone.
function fitToViewport(candidate) {
  if (typeof window === "undefined") return candidate;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const isMobile = vw < 640;
  if (isMobile) {
    // Mobile: bottom sheet feel. Full width minus 12px gutter, sits above
    // the floating launcher (which lives at bottom-20 = 80px).
    const w = Math.max(280, vw - 12);
    const h = Math.min(Math.round(vh * 0.72), 640);
    return {
      x: 6,
      y: Math.max(8, vh - h - 96),
      w,
      h,
    };
  }
  // Desktop / tablet: honour saved layout but keep panel fully on screen.
  const w = Math.max(320, Math.min(candidate.w || 400, vw - 24));
  const h = Math.max(340, Math.min(candidate.h || 620, vh - 24));
  const x = Math.max(12, Math.min(candidate.x ?? (vw - w - 24), vw - w - 12));
  const y = Math.max(12, Math.min(candidate.y ?? (vh - h - 96), vh - h - 12));
  return { x, y, w, h };
}

function useDragResize(initial) {
  const readInitial = () => {
    try {
      const raw = localStorage.getItem(CHEF_LAYOUT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return fitToViewport({ ...initial, ...parsed });
        }
      }
    } catch (_) { /* ignore */ }
    return fitToViewport(initial);
  };
  const [box, setBox] = useState(readInitial);
  // Re-fit whenever the viewport size changes (e.g. rotate an iPad from
  // portrait to landscape, or the on-screen keyboard opens on a phone).
  useEffect(() => {
    const onResize = () => setBox((b) => fitToViewport(b));
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  const stateRef = useRef({ mode: null, startX: 0, startY: 0, startBox: null });

  // Persist any layout change (throttled by React batching + a light guard).
  useEffect(() => {
    try { localStorage.setItem(CHEF_LAYOUT_KEY, JSON.stringify(box)); } catch (_) { /* ignore */ }
  }, [box]);

  const clamp = useCallback((next) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Keep at least 80px of the panel visible on every edge so it never
    // becomes unreachable off screen.
    const minW = 320, minH = 340;
    const maxW = Math.min(720, vw - 12);
    const maxH = Math.min(900, vh - 12);
    const w = Math.max(minW, Math.min(next.w, maxW));
    const h = Math.max(minH, Math.min(next.h, maxH));
    const x = Math.max(8 - w + 80, Math.min(next.x, vw - 80));
    const y = Math.max(8, Math.min(next.y, vh - 60));
    return { x, y, w, h };
  }, []);

  const onPointerMove = useCallback((e) => {
    const s = stateRef.current;
    if (!s.mode) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = clientX - s.startX;
    const dy = clientY - s.startY;
    if (s.mode === "drag") {
      setBox((b) => clamp({ ...b, x: s.startBox.x + dx, y: s.startBox.y + dy }));
    } else if (s.mode === "resize") {
      setBox((b) => clamp({ ...b, w: s.startBox.w + dx, h: s.startBox.h + dy }));
    }
    e.preventDefault();
  }, [clamp]);

  const stop = useCallback(() => {
    stateRef.current.mode = null;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  useEffect(() => {
    const move = (e) => onPointerMove(e);
    const up = () => stop();
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [onPointerMove, stop]);

  const beginDrag = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    stateRef.current = { mode: "drag", startX: clientX, startY: clientY, startBox: { ...box } };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
  };
  const beginResize = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    stateRef.current = { mode: "resize", startX: clientX, startY: clientY, startBox: { ...box } };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "nwse-resize";
    e.stopPropagation();
  };
  const resetLayout = () => setBox(fitToViewport(initial));

  return { box, beginDrag, beginResize, resetLayout };
}


// V2 True Glass wrapper: 44px blur, chromatic prism hairline, top specular.
function GlassSurface({ children, className = "", radius = 22, testId, style }) {
  return (
    <div
      data-testid={testId}
      className={`relative ${className}`}
      style={{
        borderRadius: radius,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 45%, rgba(255,255,255,0.015) 100%)",
        backdropFilter: "blur(44px) saturate(200%)",
        WebkitBackdropFilter: "blur(44px) saturate(200%)",
        border: "1px solid rgba(232,207,154,0.35)",
        boxShadow: [
          "inset 0 1px 0 rgba(255,255,255,0.32)",
          "inset 0 -1px 0 rgba(255,255,255,0.04)",
          "0 40px 90px -20px rgba(0,0,0,0.9)",
          "0 20px 40px -10px rgba(0,0,0,0.7)",
          "0 0 0 1px rgba(232,207,154,0.15)",
          "0 0 60px -20px rgba(232,207,154,0.35)",
        ].join(","),
        ...(style || {}),
      }}
    >
      <div
        aria-hidden
        className="absolute top-0 left-4 right-4 h-px rounded-full pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(232,207,154,0.9), rgba(255,255,255,0.6), rgba(90,178,201,0.85), transparent)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          borderRadius: radius,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, transparent 32%, transparent 70%, rgba(255,255,255,0.05) 100%)",
          mixBlendMode: "screen",
          opacity: 0.65,
        }}
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

function FloatingLauncher({ onOpen, unread }) {
  return (
    <button
      onClick={onOpen}
      data-testid="corporate-chef-launcher"
      aria-label="Digital Corporate Chef"
      className="fixed z-[70] flex items-center gap-2.5 rounded-full font-medium tracking-wide transition-transform hover:scale-[1.03] right-4 bottom-20 lg:right-6 lg:bottom-24 p-2.5 lg:pl-2.5 lg:pr-4 lg:py-2 text-sm"
      style={{
        background: "linear-gradient(180deg, rgba(232,207,154,0.32), rgba(200,169,106,0.14))",
        border: "1px solid rgba(232,207,154,0.55)",
        boxShadow: [
          "inset 0 1px 0 rgba(255,255,255,0.35)",
          "0 0 20px #d4af3740",
          "0 20px 44px -14px rgba(232,207,154,0.45)",
          "0 8px 18px -4px rgba(0,0,0,0.55)",
        ].join(","),
        color: "#faf3df",
      }}
    >
      <AtomCore size={30} testId="corporate-chef-atom-launcher" />
      <span
        className="hidden lg:inline"
        style={{ textShadow: "0 0 12px rgba(212,175,55,0.55), 0 0 3px rgba(255,248,225,0.35)" }}
      >
        Digital Corporate Chef
      </span>
      {unread > 0 && (
        <span
          className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-semibold"
          style={{ background: "#0a0806", color: "#e8cf9a", border: "1px solid rgba(232,207,154,0.6)" }}
        >
          {unread}
        </span>
      )}
    </button>
  );
}

function LockedTeaser({ onClose }) {
  return (
    <GlassSurface
      className="fixed z-[71] w-[340px] max-w-[92vw] p-6"
      radius={22}
      testId="corporate-chef-locked"
    >
      <div style={{ position: "absolute", top: 10, right: 10 }}>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }}
          data-testid="corporate-chef-locked-close"
        >
          <X className="w-4 h-4 text-white/70" />
        </button>
      </div>
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] mb-4"
        style={{ background: "rgba(232,207,154,0.10)", border: "1px solid rgba(232,207,154,0.35)", color: "#e8cf9a" }}
      >
        <Lock className="w-3 h-3" /> Annual Pro
      </div>
      <div className="font-display text-2xl leading-tight mb-2">Hire your Digital Corporate Chef.</div>
      <p className="text-sm text-white/70 leading-relaxed mb-5">
        A senior culinary operations consultant with live access to your recipes, roster, POS and inventory. Surgical margin advice. Weekly Monday audit report. Available on the annual Pro plan.
      </p>
      <Link
        to="/billing"
        onClick={onClose}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium"
        style={{
          background: "linear-gradient(180deg, rgba(232,207,154,0.35), rgba(200,169,106,0.12))",
          border: "1px solid rgba(232,207,154,0.55)",
          color: "#faf3df",
        }}
        data-testid="corporate-chef-upgrade-cta"
      >
        See annual plan <ArrowRight className="w-4 h-4" />
      </Link>
    </GlassSurface>
  );
}

function MessageBubble({ role, content, imageUrl }) {
  const isChef = role === "assistant";
  return (
    <div className={`flex ${isChef ? "justify-start" : "justify-end"} mb-3`}>
      <div
        className="max-w-[86%] rounded-2xl px-4 py-3 text-[13.5px] leading-relaxed"
        style={
          isChef
            ? {
                background: "linear-gradient(180deg, rgba(232,207,154,0.09), rgba(232,207,154,0.03))",
                border: "1px solid rgba(232,207,154,0.28)",
                color: "#f0ead7",
              }
            : {
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "#f0ead7",
              }
        }
        data-testid={`chef-msg-${role}`}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Plate submitted for critique"
            className="mb-2 rounded-lg max-h-48 object-cover"
            style={{ border: "1px solid rgba(232,207,154,0.28)" }}
          />
        )}
        {content.split("\n\n").map((para, i) => (
          <p key={i} className={i === 0 ? "" : "mt-2.5"}>{renderInline(para)}</p>
        ))}
      </div>
    </div>
  );
}

function renderInline(text) {
  // very small: **bold** only
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-white">{p.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

// --- Global Expansion (additive): Region picker ---------------------------
// Small dropdown that lives in the Chef panel header. Talks to the new
// backend endpoints /corporate-chef/region (GET, POST). Does not touch
// existing Profile / Settings pages.
function RegionPicker({ region, available, onChange }) {
  const [open, setOpen] = useState(false);
  const code = region?.code || "AU";
  const label = region?.label || "Australia";
  return (
    <div className="relative" data-testid="chef-region-picker">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.16em]"
        style={{ background: "rgba(232,207,154,0.10)", border: "1px solid rgba(232,207,154,0.35)", color: "#e8cf9a" }}
        title="Set the region the Chef advises against"
        data-testid="chef-region-toggle"
      >
        <Globe2 className="w-3 h-3" />
        {code}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 z-[72] w-56 rounded-xl overflow-hidden"
          style={{
            background: "rgba(10,8,6,0.96)",
            border: "1px solid rgba(232,207,154,0.35)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(232,207,154,0.12)",
          }}
          data-testid="chef-region-menu"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-white/45 border-b border-white/5">
            {label} live. {region?.detected_via === "manual" ? "Manual" : region?.detected_via === "geoip" ? "Auto detected" : "Default"}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {(available || []).map((r) => (
              <button
                key={r.code}
                onClick={async () => {
                  setOpen(false);
                  try {
                    const { data } = await api.post("/corporate-chef/region", { region: r.code });
                    onChange(data.region);
                    toast.success(`Region set to ${data.region.label}. The Chef will quote in ${data.region.currency}.`);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Could not set region.");
                  }
                }}
                className="w-full flex items-center justify-between text-left px-3 py-2 text-sm text-white/85 hover:bg-white/5"
                data-testid={`chef-region-option-${r.code}`}
              >
                <span>
                  <span className="text-white/95">{r.label}</span>
                  <span className="ml-1.5 text-[10px] text-white/40">{r.currency} : {r.unit_system}</span>
                </span>
                {r.code === code && <Check className="w-3.5 h-3.5 text-[#e8cf9a]" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CorporateChef() {
  const [ready, setReady] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [critiquing, setCritiquing] = useState(false);
  const fileInputRef = useRef(null);
  // Global Expansion (additive): region state for the picker.
  const [region, setRegion] = useState(null);
  const [regionOptions, setRegionOptions] = useState([]);
  // Operational Flex: Kitchen Status pills + active manual override banner.
  const [kitchenStatus, setKitchenStatus] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [chefIgnore, setChefIgnore] = useState(null);
  const scrollRef = useRef(null);

  // Draggable + resizable panel. Default anchors to bottom right on first
  // ever open, then remembers the operator's preferred spot in localStorage.
  const initialLayout = (() => {
    if (typeof window === "undefined") return { x: 100, y: 100, w: 400, h: 620 };
    const w = 400;
    const h = Math.min(620, Math.round(window.innerHeight * 0.7));
    return {
      x: Math.max(16, window.innerWidth - w - 24),
      y: Math.max(16, window.innerHeight - h - 156),
      w,
      h,
    };
  })();
  const { box, beginDrag, beginResize, resetLayout } = useDragResize(initialLayout);

  // Initial gate check. Runs once on mount.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/corporate-chef/status");
        setEligible(!!data?.eligible);
        setReady(true);
      } catch {
        setReady(false); // silent: unauthenticated visitors just don't see the launcher
      }
    })();
  }, []);

  // Load history when opened for the first time.
  useEffect(() => {
    if (!open || !eligible || messages.length > 0) return;
    (async () => {
      try {
        const { data } = await api.get("/corporate-chef/history");
        setMessages((data?.messages || []).map((m) => ({ role: m.role, content: m.content })));
      } catch {
        /* silent */
      }
    })();
  }, [open, eligible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Global Expansion: fetch region on first open.
  useEffect(() => {
    if (!open || !eligible || region) return;
    (async () => {
      try {
        const { data } = await api.get("/corporate-chef/region");
        setRegion(data?.region || null);
        setRegionOptions(data?.available || []);
      } catch {
        /* silent */
      }
    })();
  }, [open, eligible, region]);

  // Operational Flex: fetch Kitchen Status + any active manual override
  // marker so the UI can show the pill row and the pause banner.
  useEffect(() => {
    if (!open || !eligible) return;
    (async () => {
      try {
        const { data } = await api.get("/corporate-chef/kitchen-status");
        setKitchenStatus(data?.current || null);
        setStatusOptions(data?.available || []);
      } catch { /* silent */ }
      try {
        // The status endpoint returns the profile, not the override, so
        // we look at /status for the ignore marker (it lives on the user).
        const meRes = await api.get("/auth/me");
        setChefIgnore(meRes?.data?.chef_ignore || null);
      } catch { /* silent */ }
    })();
  }, [open, eligible, messages.length]);

  const setKitchenStatusKey = async (key) => {
    try {
      const { data } = await api.post("/corporate-chef/kitchen-status", { status: key });
      setKitchenStatus(data?.current || null);
      toast.success(`Kitchen Status set to ${data?.current?.label || key}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update Kitchen Status.");
    }
  };

  const clearManualOverride = async () => {
    try {
      await api.post("/corporate-chef/clear-override");
      setChefIgnore(null);
      toast.success("Manual override lifted. The Chef will use full history again.");
    } catch {
      toast.error("Could not clear the override.");
    }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Allow any component in the app to open the Chef panel by dispatching
  // a `cs:open-corporate-chef` window event. Used by WelcomeBackChef so
  // the "Review Audit" button pops the full consultation.
  useEffect(() => {
    const opener = (e) => {
      setOpen(true);
      // Optional: if the event carries a message to send, queue it.
      const seed = e?.detail?.seedMessage;
      if (seed && typeof seed === "string" && seed.trim()) {
        setTimeout(() => send(seed.trim()), 250);
      }
    };
    window.addEventListener("cs:open-corporate-chef", opener);
    return () => window.removeEventListener("cs:open-corporate-chef", opener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function send(text) {
    const value = (text || draft).trim();
    if (!value || sending) return;
    setDraft("");
    setMessages((prev) => [...prev, { role: "user", content: value }]);
    setSending(true);
    try {
      const { data } = await api.post("/corporate-chef/chat", { message: value });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The Corporate Chef is unavailable right now.");
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: "I could not reach the kitchen data feed for a moment. Try that question again.",
      }]);
    } finally {
      setSending(false);
    }
  }

  // Plate critique: BOH Plating and Culinary Coach (Pillar 3). Operator
  // taps the camera button, picks a plate photo, and the Chef returns
  // a Markdown critique inline in the conversation.
  async function critiquePlate(file) {
    if (!file || critiquing) return;
    // Preview thumb in the conversation so the operator sees what they sent.
    const objectUrl = URL.createObjectURL(file);
    setMessages((prev) => [
      ...prev,
      { role: "user", content: `[Plate image submitted for critique]`, image_url: objectUrl },
    ]);
    setCritiquing(true);
    try {
      const form = new FormData();
      form.append("image", file);
      const { data } = await api.post("/corporate-chef/plate-critique", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.critique }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Plate critique is unavailable right now.");
    } finally {
      setCritiquing(false);
    }
  }

  const onCameraClick = () => {
    if (critiquing) return;
    fileInputRef.current?.click();
  };
  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) critiquePlate(file);
    // Reset so the same file can be re-selected later.
    e.target.value = "";
  };

  async function clearSession() {
    if (!confirm("Start a new consultation? Prior conversation stays archived.")) return;
    try {
      await api.post("/corporate-chef/clear");
      setMessages([]);
      toast.success("New consultation started.");
    } catch {
      toast.error("Could not reset the session.");
    }
  }

  if (!ready) return null;

  if (!eligible) {
    // Show the locked teaser only on demand: no floating badge for non
    // yearly Pro users, since the sidebar already surfaces the upgrade path.
    return null;
  }

  if (!open) return <FloatingLauncher onOpen={() => setOpen(true)} unread={0} />;

  return (
    <>
      <FloatingLauncher onOpen={() => setOpen(false)} unread={0} />
      <GlassSurface
        className="fixed z-[71] flex flex-col"
        radius={22}
        testId="corporate-chef-panel"
        style={{
          left: box.x,
          top: box.y,
          width: box.w,
          maxWidth: "95vw",
          height: box.h,
          position: "fixed",
        }}
      >
        <div style={{ height: box.h, display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: 22 }}>
          {/* Header (also the drag handle). Grab anywhere on the header
              excluding the action buttons on the right, so a chef can slide
              the panel to any corner or dual monitor spot. */}
          <div
            className="flex items-start gap-3 p-4 pb-3"
            style={{
              borderBottom: "1px solid rgba(232,207,154,0.16)",
              cursor: "grab",
              touchAction: "none",
              userSelect: "none",
            }}
            onMouseDown={beginDrag}
            onTouchStart={beginDrag}
            onDoubleClick={resetLayout}
            title="Drag to move. Double click to reset position."
            data-testid="corporate-chef-drag-handle"
          >
            <GripHorizontal className="w-4 h-4 mt-1 shrink-0" style={{ color: "rgba(232,207,154,0.55)" }} />
            <div
              className="shrink-0 rounded-xl flex items-center justify-center"
              style={{
                width: 44,
                height: 44,
                background: "radial-gradient(circle at 30% 30%, rgba(255,248,225,0.18), rgba(212,175,55,0.05) 65%, transparent 100%)",
                border: "1px solid rgba(212,175,55,0.55)",
                boxShadow: "0 0 20px #d4af3740, inset 0 1px 0 rgba(255,255,255,0.28)",
              }}
            >
              <AtomCore size={30} testId="corporate-chef-atom-header" />
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="font-display text-base leading-tight"
                style={{ textShadow: "0 0 14px rgba(212,175,55,0.55), 0 0 3px rgba(255,248,225,0.35)" }}
              >
                Digital Corporate Chef
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-0.5 flex items-center gap-1.5">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ background: "#8dd3a4", boxShadow: "0 0 6px #8dd3a4" }}
                />
                Online. Annual Pro.
              </div>
            </div>
            {region && (
              <div onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                <RegionPicker region={region} available={regionOptions} onChange={setRegion} />
              </div>
            )}
            <button
              onClick={clearSession}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              title="Start new consultation"
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", cursor: "pointer" }}
              data-testid="corporate-chef-clear"
            >
              <Trash2 className="w-3.5 h-3.5 text-white/60" />
            </button>
            <button
              onClick={() => setOpen(false)}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", cursor: "pointer" }}
              data-testid="corporate-chef-close"
            >
              <X className="w-4 h-4 text-white/70" />
            </button>
          </div>

          {/* Kitchen Status pill row (Operational Flex). Small footprint so
              it does not dominate the header, but always visible so the
              operator knows which sensitivity mode is running. */}
          {statusOptions.length > 0 && (
            <div
              className="px-4 py-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em]"
              style={{ borderBottom: "1px solid rgba(232,207,154,0.10)" }}
              data-testid="kitchen-status-row"
            >
              <span className="text-white/40">Status</span>
              {statusOptions.map((opt) => {
                const active = kitchenStatus?.key === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setKitchenStatusKey(opt.key)}
                    className="px-2.5 py-1 rounded-full transition-colors"
                    style={{
                      background: active
                        ? "linear-gradient(180deg, rgba(232,207,154,0.32), rgba(200,169,106,0.10))"
                        : "rgba(255,255,255,0.03)",
                      border: active
                        ? "1px solid rgba(232,207,154,0.60)"
                        : "1px solid rgba(255,255,255,0.10)",
                      color: active ? "#faf3df" : "rgba(255,255,255,0.55)",
                      cursor: "pointer",
                    }}
                    title={opt.description}
                    data-testid={`kitchen-status-pill-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Manual override banner: shown only when the operator has an
              active `chef_ignore` marker so they know the Chef is running
              on a paused history. One click lifts the pause. */}
          {chefIgnore && (
            <div
              className="px-4 py-2 flex items-center gap-2 text-[11px]"
              style={{
                background: "rgba(212,175,55,0.06)",
                borderBottom: "1px solid rgba(212,175,55,0.24)",
                color: "rgba(232,207,154,0.85)",
              }}
              data-testid="chef-override-banner"
            >
              <span>
                History paused
                {chefIgnore.section ? `: ${chefIgnore.section.charAt(0).toUpperCase()}${chefIgnore.section.slice(1)}` : ""}
                {chefIgnore.days ? ` for the last ${chefIgnore.days} days` : ""}.
              </span>
              <button
                onClick={clearManualOverride}
                className="ml-auto px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em]"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(232,207,154,0.35)",
                  color: "#faf3df",
                  cursor: "pointer",
                }}
                data-testid="chef-override-clear"
              >
                Lift pause
              </button>
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4" data-testid="corporate-chef-thread">
            {messages.length === 0 && !sending && (
              <div>
                <div
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] mb-3"
                  style={{ background: "rgba(232,207,154,0.10)", border: "1px solid rgba(232,207,154,0.35)", color: "#e8cf9a" }}
                >
                  <Sparkles className="w-3 h-3" /> Consultation ready
                </div>
                <p className="text-sm text-white/75 leading-relaxed mb-4">
                  Twenty five years across Michelin kitchens, hotel groups, and multi site operators. I have live access to your recipes, roster, POS, and inventory. Ask me anything about your margins.
                </p>
                <div className="space-y-2">
                  {STARTERS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      className="w-full text-left rounded-xl px-3 py-2.5 text-sm text-white/85 hover:text-white transition-colors"
                      style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(232,207,154,0.18)",
                      }}
                      data-testid={`chef-starter-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <MessageBubble key={i} role={m.role} content={m.content} imageUrl={m.image_url} />
            ))}
            {sending && (
              <div className="flex justify-start mb-3">
                <div
                  className="rounded-2xl px-4 py-3 inline-flex items-center gap-2 text-xs text-white/70"
                  style={{ background: "rgba(232,207,154,0.06)", border: "1px solid rgba(232,207,154,0.22)" }}
                  data-testid="chef-thinking"
                >
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Consulting the pass
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="p-3 pt-2" style={{ borderTop: "1px solid rgba(232,207,154,0.16)" }}>
            <div
              className="flex items-end gap-2 rounded-2xl p-1.5"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(232,207,154,0.24)" }}
            >
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask the Corporate Chef anything about your kitchen."
                rows={1}
                className="flex-1 bg-transparent outline-none resize-none text-sm px-2.5 py-2 text-white/90 placeholder-white/30 max-h-32"
                data-testid="chef-input"
              />
              <button
                onClick={onCameraClick}
                disabled={critiquing || sending}
                type="button"
                title="Plate Critique: upload a dish photo for the Culinary Coach"
                className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all disabled:opacity-40"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(232,207,154,0.35)",
                  color: "#e8cf9a",
                }}
                data-testid="chef-plate-critique"
              >
                {critiquing
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Camera className="w-4 h-4" />}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                onChange={onFileChange}
                className="hidden"
                data-testid="chef-plate-critique-input"
              />
              <button
                onClick={() => send()}
                disabled={sending || !draft.trim()}
                className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all disabled:opacity-40"
                style={{
                  background: "linear-gradient(180deg, rgba(232,207,154,0.35), rgba(200,169,106,0.12))",
                  border: "1px solid rgba(232,207,154,0.55)",
                  color: "#faf3df",
                }}
                data-testid="chef-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[10px] tracking-[0.14em] uppercase text-white/35 text-center mt-2">
              Powered by senior kitchen operations logic. Advice reflects your live data.
            </div>
          </div>
        </div>

        {/* Resize grip: drag from the bottom right corner to resize the
            entire panel. Positioned fixed with the panel's computed box
            so it lives outside the GlassSurface inner stacking context.
            Double click to reset the layout. */}
      </GlassSurface>
      <div
        onMouseDown={beginResize}
        onTouchStart={beginResize}
        onDoubleClick={resetLayout}
        title="Drag to resize. Double click to reset."
        data-testid="corporate-chef-resize-handle"
        style={{
          position: "fixed",
          left: box.x + box.w - 22,
          top: box.y + box.h - 22,
          width: 20,
          height: 20,
          cursor: "nwse-resize",
          touchAction: "none",
          zIndex: 72,
          background:
            "linear-gradient(135deg, transparent 0 55%, rgba(232,207,154,0.7) 56% 62%, transparent 63% 72%, rgba(232,207,154,0.7) 73% 79%, transparent 80%)",
          borderBottomRightRadius: 20,
        }}
      />
    </>
  );
}
